// Object Type editor: create/edit form with property type rows and optimistic locking.

import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Lock, Plus, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useObjectType, useObjectTypes, useSaveObjectType } from "@/hooks/useObjectTypes";
import { useMappings } from "@/hooks/useMappings";
import { useTransformsBySource, useTransformsByTarget } from "@/hooks/useTransforms";
import { StaleVersionError, DataConstraintViolationError } from "@/types/ontology";
import { useToast } from "@/hooks/use-toast";
import { apiNameSchema, API_NAME_ERROR_MESSAGE } from "@/lib/ontology/apiNameSchema";
import { toApiName } from "@/lib/ontology/slugify";
import { IconPicker } from "./IconPicker";
import { PropertyTypeRow } from "./PropertyTypeRow";
import type { DraftPropertyType } from "./PropertyTypeRow";
import type { PropertyType } from "@/types/ontology";
import { TransformEditorSheet } from "./TransformEditorSheet";

// ── helpers ───────────────────────────────────────────────────────────────────

function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Turn a machine-parseable DB constraint-violation message into a friendly,
 * human-readable sentence naming the property and the offending row count.
 * Falls back to the raw message if it doesn't match the known shapes.
 *   required_violation: property "amount" has 3 rows with no value
 *   unique_violation: property "invoice_no" has 5 duplicate rows
 */
function describeConstraintViolation(raw: string): string {
  const required = /required_violation: property "([^"]+)" has (\d+) rows? with no value/.exec(raw);
  if (required) {
    const [, prop, count] = required;
    return `Cannot make "${prop}" required: ${count} existing object${count === "1" ? "" : "s"} ${count === "1" ? "has" : "have"} no value for it. Clean up the data first, then enable Required.`;
  }
  const unique = /unique_violation: property "([^"]+)" has (\d+) duplicate rows?/.exec(raw);
  if (unique) {
    const [, prop, count] = unique;
    return `Cannot make "${prop}" unique: ${count} existing object${count === "1" ? "" : "s"} share a duplicate value. Remove the duplicates first, then enable Unique.`;
  }
  return raw;
}

function propTosDraft(p: PropertyType): DraftPropertyType {
  return {
    id: p.id,
    name: p.name,
    api_name: p.api_name,
    kind: p.kind,
    required: p.required,
    unique_in_type: p.unique_in_type,
    enum_values: p.enum_values,
    ref_object_type_id: p.ref_object_type_id,
    currency_code: p.currency_code,
    description: p.description ?? null,
    position: p.position,
  };
}

function newDraftProperty(position: number): DraftPropertyType {
  return {
    name: "",
    api_name: "",
    kind: "string",
    required: false,
    unique_in_type: false,
    enum_values: null,
    ref_object_type_id: null,
    currency_code: null,
    description: null,
    position,
  };
}

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  api_name: apiNameSchema,
  description: z.string().nullable(),
  icon: z.string().nullable(),
  display_property: z.string().nullable(),
});

type FormValues = z.infer<typeof schema>;

// ── StaleVersionDialog (inline, mirroring scratchpad pattern) ─────────────────

interface StaleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReload: () => void;
}

function OntologyStaleVersionDialog({ open, onOpenChange, onReload }: StaleDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>This object type was edited</AlertDialogTitle>
          <AlertDialogDescription>
            Another user saved this object type while you were editing. Reload to see the latest
            version — your unsaved changes will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => {
              onReload();
              onOpenChange(false);
            }}
          >
            Reload — discard my edits
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface ObjectTypeEditorProps {
  selectedId: string | null;
  /** Called after a new type is created so the parent can switch to its ID. */
  onCreated: (id: string) => void;
}

export function ObjectTypeEditor({ selectedId, onCreated }: ObjectTypeEditorProps) {
  const isNew = selectedId === null;

  const { objectType, isLoading } = useObjectType(selectedId);
  const { objectTypes } = useObjectTypes();
  const { mappings } = useMappings(selectedId);
  const saveObjectType = useSaveObjectType();
  const { toast } = useToast();

  // Transform sections state
  const { transforms: producedByTransforms, isLoading: producedByLoading } =
    useTransformsByTarget(selectedId);
  const { transforms: usedByTransforms, isLoading: usedByLoading } =
    useTransformsBySource(selectedId);
  const [selectedTransformId, setSelectedTransformId] = useState<string | null>(null);
  const [transformSheetOpen, setTransformSheetOpen] = useState(false);

  const [properties, setProperties] = useState<DraftPropertyType[]>([]);
  const [deleteErrors, setDeleteErrors] = useState<Record<number, string>>({});
  const [propertyApiNameErrors, setPropertyApiNameErrors] = useState<Record<number, string>>({});
  const [staleDialogOpen, setStaleDialogOpen] = useState(false);
  const [constraintError, setConstraintError] = useState<string | null>(null);
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [dragToIndex, setDragToIndex] = useState<number | null>(null);

  // Track the updated_at we loaded so we can pass it for optimistic locking
  const loadedUpdatedAtRef = useRef<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      api_name: "",
      description: null,
      icon: null,
      display_property: null,
    },
  });

  // Populate form when objectType loads
  useEffect(() => {
    if (!objectType) {
      form.reset({ name: "", api_name: "", description: null, icon: null, display_property: null });
      setProperties([]);
      loadedUpdatedAtRef.current = null;
      return;
    }

    form.reset({
      name: objectType.name,
      api_name: objectType.api_name,
      description: objectType.description,
      icon: objectType.icon,
      display_property: objectType.display_property,
    });
    setProperties(objectType.property_types.map(propTosDraft));
    loadedUpdatedAtRef.current = objectType.updated_at;
  }, [objectType, form]);

  // Auto-derive api_name from name for new object types
  const watchedName = form.watch("name");
  const watchedApiName = form.watch("api_name");
  const watchedDescription = form.watch("description");
  const watchedIcon = form.watch("icon");
  const watchedDisplayProperty = form.watch("display_property");

  const isFirstSave = isNew || !objectType;

  useEffect(() => {
    if (isFirstSave && watchedName) {
      form.setValue("api_name", toApiName(watchedName), { shouldValidate: false });
    }
  }, [watchedName, isFirstSave, form]);

  // Dirty detection
  const isDirty = (() => {
    if (isNew) {
      return (
        !!watchedName ||
        !!watchedApiName ||
        !!watchedDescription ||
        !!watchedIcon ||
        properties.length > 0
      );
    }
    if (!objectType) return false;
    const formChanged =
      watchedName !== objectType.name ||
      watchedApiName !== objectType.api_name ||
      (watchedDescription ?? null) !== objectType.description ||
      (watchedIcon ?? null) !== objectType.icon ||
      (watchedDisplayProperty === "__none__" ? null : (watchedDisplayProperty ?? null)) !==
        objectType.display_property;

    const propsChanged = !deepEqual(
      properties.map((p) => ({ ...p })),
      objectType.property_types.map(propTosDraft)
    );
    return formChanged || propsChanged;
  })();

  // ── Drag & drop ────────────────────────────────────────────────────────────

  function handleDragStart(_e: React.DragEvent, index: number) {
    setDragFromIndex(index);
    setDragToIndex(null);
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    setDragToIndex(index);
  }

  function handleDrop(e: React.DragEvent, dropIndex: number) {
    e.preventDefault();
    if (dragFromIndex === null || dragFromIndex === dropIndex) return;

    setProperties((prev) => {
      const next = [...prev];
      const [removed] = next.splice(dragFromIndex, 1);
      next.splice(dropIndex, 0, removed);
      // Re-number positions
      return next.map((p, i) => ({ ...p, position: i }));
    });
    setDragFromIndex(null);
    setDragToIndex(null);
  }

  function handleDragEnd() {
    setDragFromIndex(null);
    setDragToIndex(null);
  }

  // ── Property add / change / delete ─────────────────────────────────────────

  function addProperty() {
    setProperties((prev) => [...prev, newDraftProperty(prev.length)]);
  }

  function updateProperty(index: number, updated: DraftPropertyType) {
    setProperties((prev) => prev.map((p, i) => (i === index ? updated : p)));
    // Clear any api_name error for this row when the user edits it
    setPropertyApiNameErrors((prev) => {
      if (!prev[index]) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  function deleteProperty(index: number) {
    const prop = properties[index];
    // Block delete if property api_name is referenced in any active mapping's column_map
    // column_map keys are property api_names: { property_api_name: source_column_name }
    if (prop.id && mappings.length > 0) {
      const referencedByMappings = mappings.filter((m) =>
        Object.keys(m.column_map).includes(prop.api_name)
      );
      if (referencedByMappings.length > 0) {
        const names = referencedByMappings.map((m) => `"${m.name}"`).join(", ");
        setDeleteErrors((prev) => ({
          ...prev,
          [index]: `Cannot delete — referenced by mapping${referencedByMappings.length > 1 ? "s" : ""}: ${names}`,
        }));
        return;
      }
    }
    setDeleteErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setProperties((prev) =>
      prev.filter((_, i) => i !== index).map((p, i) => ({ ...p, position: i }))
    );
  }

  // ── Save ───────────────────────────────────────────────────────────────────

  async function handleSave(values: FormValues) {
    // Validate property api_names before calling RPC.
    // If the property has neither an explicit api_name nor a name to derive from,
    // keep effectiveApiName as "" so apiNameSchema rejects it (requires min length 1).
    const apiNameErrs: Record<number, string> = {};
    properties.forEach((p, i) => {
      const effectiveApiName = p.api_name || (p.name ? toApiName(p.name) : "");
      const result = apiNameSchema.safeParse(effectiveApiName);
      if (!result.success) {
        apiNameErrs[i] = result.error.issues[0]?.message ?? API_NAME_ERROR_MESSAGE;
      }
    });
    setPropertyApiNameErrors(apiNameErrs);
    if (Object.keys(apiNameErrs).length > 0) return;

    // Clear any prior constraint-violation banner before re-attempting.
    setConstraintError(null);

    const displayPropValue =
      values.display_property === "__none__" ? null : (values.display_property ?? null);

    try {
      const result = await saveObjectType.mutateAsync({
        id: selectedId,
        expectedUpdatedAt: loadedUpdatedAtRef.current,
        input: {
          name: values.name,
          api_name: values.api_name,
          description: values.description ?? null,
          icon: values.icon ?? null,
          display_property: displayPropValue,
          property_types: properties.map((p, i) => ({
            ...(p.id ? { id: p.id } : {}),
            name: p.name,
            api_name: p.api_name || (p.name ? toApiName(p.name) : ""),
            kind: p.kind,
            required: p.required,
            unique_in_type: p.unique_in_type,
            enum_values: p.enum_values,
            ref_object_type_id: p.ref_object_type_id,
            currency_code: p.currency_code,
            description: p.description ?? null,
            position: i,
          })),
        },
      });

      if (isNew && result.id) {
        onCreated(result.id);
      }
    } catch (err) {
      if (err instanceof StaleVersionError) {
        setStaleDialogOpen(true);
      } else if (err instanceof DataConstraintViolationError) {
        // Existing data would violate a newly-enabled required/unique flag.
        // Show a friendly, property-named message inline + as a toast. NOT saved.
        const friendly = describeConstraintViolation(err.message);
        setConstraintError(friendly);
        toast({
          title: "Save blocked — existing data conflicts",
          description: friendly,
          variant: "destructive",
        });
      }
      // Other errors are toasted by the hook
    }
  }

  function handleReload() {
    // Invalidation already triggered by useSaveObjectType.onSuccess; just re-trigger
    // by resetting to loaded values — useEffect on objectType will re-populate.
    loadedUpdatedAtRef.current = null;
    form.reset({
      name: objectType?.name ?? "",
      api_name: objectType?.api_name ?? "",
      description: objectType?.description ?? null,
      icon: objectType?.icon ?? null,
      display_property: objectType?.display_property ?? null,
    });
    setProperties(objectType?.property_types.map(propTosDraft) ?? []);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!isNew && isLoading) {
    return (
      <div className="p-6 space-y-4 flex-1">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  // Nothing selected and not new mode
  if (!isNew && !objectType && !isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center space-y-2">
          <Layers className="w-10 h-10 mx-auto opacity-40 text-muted-foreground" />
          <p className="text-sm font-medium">Select an object type</p>
          <p className="text-xs text-muted-foreground">Choose from the list or create a new one.</p>
        </div>
      </div>
    );
  }

  const displayPropertyOptions = properties.filter((p) => p.api_name);

  return (
    <>
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSave)}
            className="space-y-6"
            data-testid="object-type-form"
          >
            {/* ── Header section ────────────────────────────────────────────── */}
            <div className="space-y-4">
              <h3 className="text-base font-display font-semibold">
                {isNew ? "New Object Type" : "Edit Object Type"}
              </h3>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs text-muted-foreground">Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Vendor" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* api_name */}
                <FormField
                  control={form.control}
                  name="api_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs text-muted-foreground flex items-center gap-1">
                        API Name
                        {!isFirstSave && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Lock
                                className="w-3 h-3 text-muted-foreground"
                                aria-label="Immutable"
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              api_name is immutable. Create a new type to rename.
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="vendor"
                          {...field}
                          disabled={!isFirstSave}
                          className={!isFirstSave ? "font-mono text-muted-foreground" : "font-mono"}
                          data-testid="api-name-input"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-muted-foreground">Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe this object type..."
                        className="resize-none"
                        rows={2}
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Icon + display_property row */}
              <div className="flex flex-wrap items-end gap-4">
                {/* Icon */}
                <FormField
                  control={form.control}
                  name="icon"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs text-muted-foreground">Icon</FormLabel>
                      <FormControl>
                        <IconPicker
                          value={field.value ?? null}
                          onChange={(icon) => field.onChange(icon)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Display property */}
                <FormField
                  control={form.control}
                  name="display_property"
                  render={({ field }) => (
                    <FormItem className="flex-1 min-w-[160px]">
                      <FormLabel className="text-xs text-muted-foreground">
                        Display Property
                      </FormLabel>
                      <Select
                        value={field.value ?? "__none__"}
                        onValueChange={(v) => field.onChange(v === "__none__" ? null : v)}
                      >
                        <FormControl>
                          <SelectTrigger aria-label="Display property">
                            <SelectValue placeholder="Default (primary key)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="__none__">Default (primary key)</SelectItem>
                          {displayPropertyOptions.map((p) => (
                            <SelectItem key={p.api_name} value={p.api_name}>
                              {p.name || p.api_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* ── Property types ─────────────────────────────────────────────── */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Properties</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addProperty}
                  className="gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Property
                </Button>
              </div>

              {properties.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4 border border-dashed rounded-md">
                  No properties yet. Add one above.
                </p>
              ) : (
                <div className="space-y-2">
                  {/* Column headers — widths mirror PropertyTypeRow layout */}
                  <div
                    className="flex items-center gap-2 px-2 text-xs font-medium text-muted-foreground select-none"
                    aria-hidden="true"
                    data-testid="property-columns-header"
                  >
                    {/* drag grip placeholder */}
                    <span className="w-4 flex-shrink-0" />
                    <span className="w-48 flex-shrink-0">Name</span>
                    <span className="w-40 flex-shrink-0 hidden md:block">API name</span>
                    <span className="w-28 flex-shrink-0">Type</span>
                    <span className="w-16 flex-shrink-0">Required</span>
                    <span className="w-16 flex-shrink-0">Unique</span>
                    <span className="flex-1 min-w-0">Values</span>
                    <span className="w-40 flex-shrink-0">Description</span>
                    {/* delete button placeholder */}
                    <span className="w-7 flex-shrink-0" />
                  </div>
                  {properties.map((prop, i) => (
                    <PropertyTypeRow
                      key={prop.id ?? `new-${i}`}
                      property={prop}
                      index={i}
                      onChange={(updated) => updateProperty(i, updated)}
                      onDelete={() => deleteProperty(i)}
                      deleteError={deleteErrors[i] ?? null}
                      apiNameError={propertyApiNameErrors[i] ?? null}
                      objectTypes={objectTypes.filter((ot) => ot.id !== selectedId)}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                      isDragTarget={dragToIndex === i}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ── Constraint-violation banner ─────────────────────────────────── */}
            {constraintError && (
              <p
                role="alert"
                className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
                data-testid="constraint-error"
              >
                {constraintError}
              </p>
            )}

            {/* ── Save bar ────────────────────────────────────────────────────── */}
            <div className="flex items-center gap-3 pt-2 border-t border-border sticky bottom-0 bg-background/80 backdrop-blur pb-2">
              <Button
                type="submit"
                disabled={!isDirty || saveObjectType.isPending}
                className="gap-2"
                data-testid="save-button"
              >
                {saveObjectType.isPending ? "Saving..." : "Save"}
              </Button>
              {isDirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
            </div>
          </form>
        </Form>

        {/* ── Transforms: Produced by ──────────────────────────────────────── */}
        {!isNew && (
          <div className="pt-4 border-t border-border space-y-2" data-testid="produced-by-section">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Produced by</h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => {
                  setSelectedTransformId(null);
                  setTransformSheetOpen(true);
                }}
              >
                + New transform
              </Button>
            </div>

            {producedByLoading ? (
              <div className="space-y-1">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : producedByTransforms.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No transforms produce this Object Type.
              </p>
            ) : (
              <div className="space-y-1">
                {producedByTransforms.map((t) => (
                  <button
                    key={t.id}
                    className="w-full text-left text-xs px-3 py-2 rounded-md border border-border hover:bg-muted/40 transition-colors"
                    onClick={() => {
                      setSelectedTransformId(t.id);
                      setTransformSheetOpen(true);
                    }}
                  >
                    <span className="font-medium">{t.name}</span>
                    <span className="text-muted-foreground ml-2 font-mono">{t.api_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Transforms: Used by ──────────────────────────────────────────── */}
        {!isNew && (
          <div className="pt-4 border-t border-border space-y-2" data-testid="used-by-section">
            <h4 className="text-sm font-medium">Used by</h4>

            {usedByLoading ? (
              <div className="space-y-1">
                <Skeleton className="h-8 w-full" />
              </div>
            ) : usedByTransforms.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No transforms use this Object Type as source.
              </p>
            ) : (
              <div className="space-y-1">
                {usedByTransforms.map((t) => (
                  <button
                    key={t.id}
                    className="w-full text-left text-xs px-3 py-2 rounded-md border border-border hover:bg-muted/40 transition-colors"
                    onClick={() => {
                      setSelectedTransformId(t.id);
                      setTransformSheetOpen(true);
                    }}
                  >
                    <span className="font-medium">{t.name}</span>
                    <span className="text-muted-foreground ml-2 font-mono">{t.api_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stale version dialog */}
      <OntologyStaleVersionDialog
        open={staleDialogOpen}
        onOpenChange={setStaleDialogOpen}
        onReload={handleReload}
      />

      {/* Transform editor sheet */}
      <TransformEditorSheet
        open={transformSheetOpen}
        onOpenChange={setTransformSheetOpen}
        transformId={selectedTransformId}
        defaultSourceObjectTypeId={selectedId ?? undefined}
        onCreated={(id) => {
          setSelectedTransformId(id);
        }}
      />
    </>
  );
}
